export const compliance = {
  async compliance() {
    let controls = { data: [], grouped: {} };
    let implementations = { data: [], summary: {} };
    let assets = { data: [] };
    try {
      [controls, implementations, assets] = await Promise.all([
        API.getControls(),
        API.getImplementations(),
        API.getAssets(),
      ]);
    } catch (e) { /* empty */ }

    // Stash fetched assets + implementations on the module so openAssignModal
    // can reuse them without an extra request.
    compliance._assets = assets.data || [];
    compliance._implsByControl = {};
    for (const impl of (implementations.data || [])) {
      (compliance._implsByControl[impl.control_id] ||= []).push(impl);
    }

    const assetNameById = Object.fromEntries((assets.data || []).map((a) => [a.id, a.name]));

    const families = ['Govern', 'Map', 'Measure', 'Manage'];
    const familyDescriptions = {
      Govern: 'Organizational governance structures and policies',
      Map: 'AI system categorization and context mapping',
      Measure: 'Risk assessment and performance measurement',
      Manage: 'Risk mitigation and ongoing management',
    };

    function implsSummaryCell(impls) {
      if (!impls || impls.length === 0) return '<span class="badge badge-draft">Gap</span>';
      // When all implementations share a single status, show that status; when
      // mixed, show the strongest one (implemented > partially > planned > n/a).
      const order = ['implemented', 'partially_implemented', 'planned', 'not_applicable'];
      const seen = new Set(impls.map((i) => i.implementation_status));
      const best = order.find((s) => seen.has(s)) || impls[0].implementation_status;
      return App.badge(best);
    }

    function scopeCell(impls) {
      if (!impls || impls.length === 0) return '<span class="text-muted" style="font-size:11px">— no implementation</span>';
      const scoped = impls.filter((i) => i.ai_asset_id);
      const tenantWide = impls.filter((i) => !i.ai_asset_id).length;
      if (scoped.length === 0 && tenantWide > 0) return '<span class="badge badge-info">Tenant-wide</span>';
      const pills = scoped.slice(0, 3).map((i) => {
        const name = assetNameById[i.ai_asset_id] || i.asset_name || i.ai_asset_id.slice(0, 8);
        return `<span class="meta-pill" title="${name}" style="font-size:10px">${name}</span>`;
      }).join(' ');
      const more = scoped.length > 3 ? ` <span class="text-muted" style="font-size:10px">+${scoped.length - 3} more</span>` : '';
      const tenant = tenantWide > 0 ? ' <span class="badge badge-info" style="font-size:10px">+ tenant-wide</span>' : '';
      return pills + more + tenant;
    }

    return `
      <div class="page-header">
        <div><h2>Compliance Management</h2><p>Multi-framework compliance mapping: NIST AI RMF, FDA SaMD, ONC HTI-1, HIPAA. Click any control's Status to assign or edit implementations (scoped tenant-wide or to a specific AI asset).</p></div>
      </div>
      <div class="stats-row">
        <div class="stat-card success"><div class="stat-value">${implementations.summary.compliance_percentage || 0}%</div><div class="stat-label">Overall Compliance</div></div>
        <div class="stat-card info"><div class="stat-value">${implementations.summary.implemented || 0}</div><div class="stat-label">Implemented</div></div>
        <div class="stat-card warning"><div class="stat-value">${implementations.summary.partially_implemented || 0}</div><div class="stat-label">Partially Implemented</div></div>
        <div class="stat-card"><div class="stat-value">${implementations.summary.planned || 0}</div><div class="stat-label">Planned</div></div>
      </div>
      ${families.map(family => `
        <div class="card" style="margin-bottom:16px">
          <div class="card-header">
            <h3>${family} - ${familyDescriptions[family]}</h3>
            <span class="badge badge-info">${(controls.grouped?.[family] || []).length} controls</span>
          </div>
          <div class="table-container">
            <table>
              <thead><tr><th>Control ID</th><th>Title</th><th>NIST AI RMF</th><th>HIPAA</th><th>Applies To</th><th>Status</th></tr></thead>
              <tbody>
                ${(controls.grouped?.[family] || []).map(c => {
                  const impls = compliance._implsByControl[c.id] || [];
                  return `<tr>
                    <td><strong>${c.control_id}</strong></td>
                    <td>${c.title}</td>
                    <td>${c.nist_ai_rmf_ref || '-'}</td>
                    <td>${c.hipaa_ref || '-'}</td>
                    <td>${scopeCell(impls)}</td>
                    <td><button type="button" class="btn-link compliance-assign" data-control-id="${c.id}" data-control-code="${c.control_id}" data-control-title="${(c.title || '').replace(/"/g, '&quot;')}" title="Assign or edit implementation">${implsSummaryCell(impls)}</button></td>
                  </tr>`;
                }).join('') || `<tr><td colspan="6" class="empty-state">No controls loaded.</td></tr>`}
              </tbody>
            </table>
          </div>
        </div>
      `).join('')}`;
  },

  async complianceBind() {
    document.querySelectorAll('.compliance-assign').forEach((btn) => {
      btn.addEventListener('click', () => compliance.openAssignModal(btn.dataset));
    });
  },

  /**
   * Modal supports multiple per-asset implementations of the same control.
   * Existing rows are listed with inline Edit + Remove; a single form at
   * the bottom adds a new implementation (asset-scoped or tenant-wide).
   */
  openAssignModal(data) {
    const controlId = data.controlId;
    const impls = (compliance._implsByControl[controlId] || []).slice();
    const assets = compliance._assets || [];
    const assetOptions = assets.map((a) =>
      `<option value="${a.id}">${a.name}</option>`
    ).join('');
    const implRows = impls.length === 0
      ? '<p style="color:var(--text-muted);font-size:13px;margin:0">No implementations yet — scope this control tenant-wide or pick an asset below.</p>'
      : impls.map((i) => {
        const scope = i.ai_asset_id
          ? `<span class="meta-pill">Asset: ${i.asset_name || (assets.find((a) => a.id === i.ai_asset_id)?.name) || i.ai_asset_id.slice(0, 8)}</span>`
          : '<span class="badge badge-info">Tenant-wide</span>';
        return `<div class="impl-row" data-impl-id="${i.id}" style="display:flex;gap:8px;align-items:center;justify-content:space-between;padding:8px 10px;border:1px solid var(--border);border-radius:var(--radius);margin-bottom:6px">
          <div>${scope} ${App.badge(i.implementation_status)}</div>
          <div style="display:flex;gap:6px">
            <button type="button" class="btn btn-sm btn-outline impl-edit" data-impl-id="${i.id}" data-impl-status="${i.implementation_status}" data-impl-asset="${i.ai_asset_id || ''}" data-impl-details="${(i.implementation_details || '').replace(/"/g, '&quot;')}">Edit</button>
          </div>
        </div>`;
      }).join('');

    App.openModal(
      `Assign implementation — ${data.controlCode}`,
      `
      <form id="ci-form">
        <div class="form-group"><label>Control</label><input type="text" value="${data.controlCode} · ${data.controlTitle}" readonly></div>

        <div class="form-section" style="margin-top:8px">
          <h4 style="font-size:13px;margin:0 0 6px 0">Existing implementations</h4>
          <div id="ci-impl-list">${implRows}</div>
        </div>

        <hr style="margin:14px 0" />

        <h4 style="font-size:13px;margin:0 0 8px 0" id="ci-form-heading">Add implementation</h4>
        <input type="hidden" id="ci-impl-id" value="">
        <div class="form-group">
          <label for="ci-asset">Scope</label>
          <select id="ci-asset" class="form-control">
            <option value="">Tenant-wide (applies to all assets)</option>
            ${assetOptions}
          </select>
        </div>
        <div class="form-group">
          <label for="ci-status">Implementation status</label>
          <select id="ci-status" class="form-control">
            ${['implemented', 'partially_implemented', 'planned', 'not_applicable'].map((s) =>
              `<option value="${s}" ${s === 'planned' ? 'selected' : ''}>${App.formatLabel(s)}</option>`
            ).join('')}
          </select>
        </div>
        <div class="form-group">
          <label for="ci-details" style="display:flex;align-items:center;justify-content:space-between">
            <span>Implementation details</span>
            <button type="button" class="btn btn-sm btn-outline" id="ci-forgeml" title="Generate a starter narrative using ForgeML">Suggest with ForgeML</button>
          </label>
          <textarea id="ci-details" rows="5" placeholder="How this control is satisfied, responsible team, evidence locations, testing cadence..."></textarea>
          <div id="ci-forgeml-choices" style="display:none;margin-top:8px"></div>
        </div>
      </form>
      `,
      `<button class="btn btn-primary" id="ci-submit">Assign</button>`,
    );

    // Wire edit buttons for existing impls (populates the form; submit does PUT).
    document.querySelectorAll('#ci-impl-list .impl-edit').forEach((btn) => {
      btn.addEventListener('click', () => {
        document.getElementById('ci-impl-id').value = btn.dataset.implId;
        document.getElementById('ci-asset').value = btn.dataset.implAsset || '';
        document.getElementById('ci-status').value = btn.dataset.implStatus || 'planned';
        document.getElementById('ci-details').value = btn.dataset.implDetails || '';
        document.getElementById('ci-form-heading').textContent = 'Edit implementation';
        document.getElementById('ci-submit').textContent = 'Save';
      });
    });

    // ForgeML suggest. Storing suggestion bodies in a module-level array
    // keyed by integer index is the simplest thing that cannot fail: no
    // encoding round-trip, no closure-capture issues across re-renders.
    // Event delegation on the choices container so re-clicks Suggest don't
    // orphan listeners.
    compliance._lastSuggestions = [];
    const choicesEl = document.getElementById('ci-forgeml-choices');
    choicesEl.addEventListener('click', (ev) => {
      const pick = ev.target.closest('.forgeml-pick');
      if (!pick) return;
      const idx = Number(pick.dataset.idx);
      const body = compliance._lastSuggestions[idx];
      if (!body) return;
      const detailsEl = document.getElementById('ci-details');
      if (!detailsEl) return;
      detailsEl.value = body;
      detailsEl.dispatchEvent(new Event('input', { bubbles: true }));
      detailsEl.focus();
      detailsEl.classList.add('forgeml-applied');
      setTimeout(() => detailsEl.classList.remove('forgeml-applied'), 800);
      if (App?.toast) App.toast('Copied suggestion into Details', 'success');
    });

    document.getElementById('ci-forgeml').addEventListener('click', async () => {
      choicesEl.style.display = 'block';
      choicesEl.innerHTML = '<div class="text-muted" style="font-size:12px">Generating suggestions…</div>';
      try {
        const resp = await API.mlAssist({
          field_type: 'control_implementation',
          context: {
            control_id: data.controlCode,
            control_title: data.controlTitle,
          },
        });
        const suggestions = (resp.data?.suggestions || []);
        if (!suggestions.length) {
          choicesEl.innerHTML = '<div class="text-muted" style="font-size:12px">No suggestions returned.</div>';
          return;
        }
        // Stash the full bodies on the module so the click handler can look
        // them up by index. Cleared on next Suggest click.
        compliance._lastSuggestions = suggestions.map((s) => s.body || '');
        const mode = resp.data?.mode === 'llm' ? 'LLM (ForgeML)' : 'Templates';
        const escHtml = (str) => String(str || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
        choicesEl.innerHTML = `
          <div style="font-size:11px;color:var(--text-muted);margin-bottom:6px">${mode} — click a suggestion to copy it into Details</div>
          ${suggestions.map((s, i) => {
            const preview = (s.body || '').slice(0, 160) + ((s.body || '').length > 160 ? '…' : '');
            return `
              <button type="button" class="btn btn-sm btn-outline forgeml-pick" data-idx="${i}" style="display:block;width:100%;text-align:left;margin-bottom:6px;padding:8px 10px;white-space:normal;font-weight:500">
                <div style="font-size:12px;margin-bottom:2px;pointer-events:none">${escHtml(s.title || 'Suggestion')}</div>
                <div style="font-size:11px;color:var(--text-secondary);font-weight:400;pointer-events:none">${escHtml(preview)}</div>
              </button>`;
          }).join('')}`;
      } catch (err) {
        choicesEl.innerHTML = `<div class="text-muted" style="font-size:12px;color:var(--danger)">ForgeML failed: ${err.message}</div>`;
      }
    });

    // Submit = create new or update existing depending on hidden id.
    document.getElementById('ci-submit').addEventListener('click', async () => {
      const implId = document.getElementById('ci-impl-id').value;
      const assetId = document.getElementById('ci-asset').value || null;
      const body = {
        control_id: controlId,
        ai_asset_id: assetId,
        implementation_status: document.getElementById('ci-status').value,
        implementation_details: document.getElementById('ci-details').value || null,
      };
      try {
        if (implId) {
          await API.updateImplementation(implId, body);
          App.toast('Implementation updated', 'success');
        } else {
          await API.createImplementation(body);
          App.toast('Implementation assigned', 'success');
        }
        App.closeModal();
        App.navigate('compliance');
      } catch (err) { App.toast(err.message, 'error'); }
    });
  },
};
